import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Extra text matched by the filter but not displayed (codes, aliases). */
  keywords?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Rendered at the top of the list; picking it emits ''. */
  clearLabel?: string;
}

/**
 * Select2-style combobox: a popover with a text filter over the options.
 * Built for long masters lists (materials, warehouses) where a plain Select
 * becomes unusable (field report 2026-07-31). Filtering is a case-insensitive
 * substring match over label + keywords.
 */
export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  clearLabel,
}: SearchableSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [filter, setFilter] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const normalized = filter.trim().toLowerCase();
  const visible = normalized
    ? options.filter((o) => `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(normalized))
    : options;

  React.useEffect(() => {
    if (open) {
      setFilter('');
      setActiveIndex(0);
    }
  }, [open]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [normalized]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = visible[activeIndex];
      if (option) pick(option.value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    // modal: inside a Radix Dialog the dialog's focus trap would otherwise
    // fight the portal'd content and instantly dismiss the popover.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{selected ? selected.label : (placeholder ?? '—')}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      {/* Default open-autofocus lands on the filter input (first focusable
          child). Overriding it with preventDefault left focus on the trigger,
          which Radix treats as focus-outside and instantly dismisses. */}
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-64 p-1" align="start">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('efs.common.search.placeholder', {
            ns: 'common',
            defaultValue: '検索…',
          })}
          className="mb-1 h-8"
        />
        <div ref={listRef} className="max-h-64 overflow-y-auto">
          {clearLabel ? (
            <button
              type="button"
              onClick={() => pick('')}
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
            >
              {clearLabel}
            </button>
          ) : null}
          {visible.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">
              {t('efs.common.messages.empty', { ns: 'common' })}
            </p>
          ) : (
            visible.map((option, index) => (
              <button
                key={`${option.value}:${index}`}
                type="button"
                data-index={index}
                onClick={() => pick(option.value)}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                  index === activeIndex && 'bg-accent',
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    option.value === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="truncate">{option.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
